import type { SimpleTableConfig, SimpleFieldConfig } from "../UniversalCRMTable/types";

export interface AutoImportColumn {
  key: string;
  label: string;
  required: boolean;
  description: string;
  validate: (value: any) => string | null;
}

export function generateFieldDescription(fieldConfig: SimpleFieldConfig): string {
  let description = fieldConfig.label;

  if (fieldConfig.required) {
    description += " (required)";
  }

  if (fieldConfig.options && fieldConfig.options.length > 0) {
    const optionValues = fieldConfig.options.map((opt) => opt.value).join(", ");
    description += ` - Options: ${optionValues}`;
  }

  if (fieldConfig.type === "date") {
    description += " - Format: YYYY-MM-DD";
  }

  if (fieldConfig.type === "datetime") {
    description += " - Format: YYYY-MM-DD HH:MM:SS";
  }

  if (fieldConfig.type === "boolean") {
    description += " - Use: true/false, yes/no, 1/0";
  }

  return description;
}

export function generateFieldValidation(
  fieldConfig: SimpleFieldConfig
): (value: any) => string | null {
  return (value: any) => {
    if (!fieldConfig.required && (!value || value === "")) {
      return null;
    }

    if (fieldConfig.required && (!value || value.toString().trim() === "")) {
      return `${fieldConfig.label} is required`;
    }

    if (
      fieldConfig.type === "text" &&
      (fieldConfig.label.toLowerCase().includes("email") ||
        (typeof value === "string" && value.includes("@")))
    ) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return "Please enter a valid email address";
      }
    }

    if (fieldConfig.type === "number") {
      const num = Number(value);
      if (isNaN(num)) {
        return `${fieldConfig.label} must be a valid number`;
      }
      if (fieldConfig.min !== undefined && num < fieldConfig.min) {
        return `${fieldConfig.label} must be at least ${fieldConfig.min}`;
      }
      if (fieldConfig.max !== undefined && num > fieldConfig.max) {
        return `${fieldConfig.label} must be no more than ${fieldConfig.max}`;
      }
    }

    if (fieldConfig.type === "date" || fieldConfig.type === "datetime") {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return `${fieldConfig.label} must be a valid date`;
      }
    }

    if (fieldConfig.type === "boolean") {
      const boolValues = ["true", "false", "yes", "no", "1", "0", "on", "off"];
      if (!boolValues.includes(value.toString().toLowerCase())) {
        return `${fieldConfig.label} must be one of: ${boolValues.join(", ")}`;
      }
    }

    if (fieldConfig.options && fieldConfig.options.length > 0) {
      const validValues = fieldConfig.options.map((opt) => opt.value);
      if (!validValues.includes(value)) {
        return `${fieldConfig.label} must be one of: ${validValues.join(", ")}`;
      }
    }

    return null;
  };
}

export function generateSampleRows(
  columns: AutoImportColumn[],
  config: SimpleTableConfig<any>
): string {
  const row1Values: string[] = [];
  const row2Values: string[] = [];

  columns.forEach(({ key }) => {
    const fieldConfig = config.fields[key];
    const sampleData = generateSampleFieldValue(key, fieldConfig, config.entity);

    row1Values.push(`"${sampleData.sample1}"`);
    row2Values.push(`"${sampleData.sample2}"`);
  });

  return `${row1Values.join(",")}\n${row2Values.join(",")}`;
}

function generateSampleFieldValue(
  fieldKey: string,
  fieldConfig: SimpleFieldConfig,
  _entityType: string
): { sample1: string; sample2: string } {
  if (fieldConfig.options && fieldConfig.options.length > 0) {
    return {
      sample1: fieldConfig.options[0].value,
      sample2: fieldConfig.options[Math.min(1, fieldConfig.options.length - 1)].value,
    };
  }

  const lowerKey = fieldKey.toLowerCase();

  if (lowerKey.includes("name")) {
    return { sample1: "John Doe", sample2: "Jane Smith" };
  }
  if (lowerKey.includes("email")) {
    return { sample1: "john.doe@example.com", sample2: "jane.smith@example.com" };
  }
  if (lowerKey.includes("phone")) {
    return { sample1: "123-456-7890", sample2: "098-765-4321" };
  }
  if (lowerKey.includes("title") || lowerKey.includes("subject")) {
    return { sample1: "Sample Title 1", sample2: "Sample Title 2" };
  }
  if (lowerKey.includes("description") || lowerKey.includes("notes")) {
    return { sample1: "Sample description", sample2: "Another description" };
  }
  if (lowerKey.includes("company")) {
    return { sample1: "Acme Corp", sample2: "Tech Solutions Inc" };
  }
  if (lowerKey.includes("priority")) {
    return { sample1: "high", sample2: "medium" };
  }
  if (lowerKey.includes("status")) {
    return { sample1: "active", sample2: "pending" };
  }
  if (lowerKey.includes("date")) {
    const date1 = new Date();
    const date2 = new Date(date1.getTime() + 24 * 60 * 60 * 1000);
    return {
      sample1: date1.toISOString().split("T")[0],
      sample2: date2.toISOString().split("T")[0],
    };
  }
  if (fieldConfig.type === "number") {
    return { sample1: "100", sample2: "200" };
  }
  if (fieldConfig.type === "boolean") {
    return { sample1: "true", sample2: "false" };
  }

  return { sample1: `Sample ${fieldConfig.label} 1`, sample2: `Sample ${fieldConfig.label} 2` };
}

export function transformFieldValue(value: any, fieldConfig: SimpleFieldConfig): any {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  const stringValue = value.toString().trim();

  switch (fieldConfig.type) {
    case "number": {
      const num = Number(stringValue);
      return isNaN(num) ? undefined : num;
    }
    case "boolean": {
      const lowerValue = stringValue.toLowerCase();
      if (["true", "yes", "1", "on"].includes(lowerValue)) return true;
      if (["false", "no", "0", "off"].includes(lowerValue)) return false;
      return undefined;
    }
    case "date":
    case "datetime": {
      const date = new Date(stringValue);
      return isNaN(date.getTime()) ? undefined : date.toISOString();
    }
    default:
      return stringValue;
  }
}
